import {
  addMonths,
  type BankAccountType,
  LABELS,
  maskAccountNumber,
  todayInTimezone,
  toDecimal,
} from '@mm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowRight, Info, RotateCcw, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { AppIcon, AppIcons } from '../../components/AppIcon';
import { Button } from '../../components/ui/Button';
import { Alert, Badge, Card, EmptyState, PageHeader, Spinner } from '../../components/ui/display';
import { Input } from '../../components/ui/inputs';
import { useToast } from '../../components/ui/Toast';
import { useAccountDisplayMode } from '../../hooks/useAccountDisplayMode';
import { accountLabel } from '../../utils/accounts';
import { formatDecimal, formatMoney, formatPercent, monthName } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';

// ── API shapes ───────────────────────────────────────────────────────

interface AppRef {
  id: string;
  name: string;
  iconUrl: string | null;
  developerName: string | null;
}

interface Figures {
  estimatedRevenue: string;
  actualRevenue: string;
  revenueDifference: string;
  estimatedSpend: string;
  actualSpend: string;
  spendDifference: string;
  estimatedGst: string;
  actualGst: string;
  estimatedProfit: string;
  actualProfit: string;
  profitDifference: string;
}

interface RevenueRow {
  account: {
    id: string;
    accountName: string;
    accountIdentifier: string | null;
    currencyCode: string;
    revenueSource: { id: string; name: string; code: string };
  };
  /** BELOW_THRESHOLD: unpaid balance under the payment threshold, carried to next month. */
  status: 'NOT_ENTERED' | 'BELOW_THRESHOLD' | 'BANK_PENDING' | 'RECEIVED';
  /** This month's estimate (daily entries). */
  estimatedAmount: string;
  estimatedInr: string;
  payoutThreshold: string | null;
  /** Unpaid earlier months included in this payout. */
  carriedAmount: string;
  carriedInr: string;
  carriedMonths: string[];
  /** Carried + this month: what the payout is compared with. */
  balanceAmount: string;
  balanceInr: string;
  /** Bank account active on the received date (or today). */
  bank: {
    bankName: string;
    accountNumber: string;
    accountHolderName: string | null;
    accountType: BankAccountType | null;
  } | null;
  settlement: {
    actualAmount: string;
    actualInr: string | null;
    receivedDate: string | null;
    reference: string | null;
    updatedAt: string;
  } | null;
  estimatedRate: string | null;
  deduction: string | null;
  deductionPercent: string | null;
  actualRate: string | null;
  actualInr: string;
  deductionImpactInr: string | null;
  exchangeImpactInr: string | null;
  differenceInr: string;
  needsRate: boolean;
  apps: AppRef[];
}

interface AdsRow {
  account: {
    id: string;
    accountName: string;
    accountIdentifier: string | null;
    currencyCode: string;
    platform: { id: string; name: string; code: string };
  };
  status: 'NOT_ENTERED' | 'ENTERED';
  settlement: { invalidClickCredit: string; updatedAt: string } | null;
  estimatedAmount: string;
  estimatedInr: string;
  estimatedGst: string;
  creditInr: string;
  creditGst: string;
  actualInr: string;
  actualGst: string;
  apps: AppRef[];
}

interface MonthEndData {
  year: number;
  month: number;
  editable: boolean;
  revenue: RevenueRow[];
  ads: AdsRow[];
  apps: (Figures & { app: AppRef })[];
  totals: Figures;
  progress: {
    revenueAccounts: number;
    revenueEntered: number;
    revenueCarried: number;
    revenueReceived: number;
    adsAccounts: number;
    adsEntered: number;
  };
}

// ── Helpers ──────────────────────────────────────────────────────────

const AMOUNT_RE = /^\d{1,14}(\.\d{1,4})?$/;
const plain = (v: string | null | undefined) => (v == null ? '' : String(Number(v)));
const sign = (v: string | null | undefined) => (v == null ? 0 : toDecimal(v).comparedTo(0));

/** Difference in green (better) or red (worse); `inverse` for costs. */
function Diff({ value, inverse = false }: { value: string | null; inverse?: boolean }) {
  const s = sign(value);
  if (value == null) return <span className="text-slate-400">—</span>;
  if (s === 0) return <span className="num text-slate-400">₹0.00</span>;
  const good = inverse ? s < 0 : s > 0;
  return (
    <span className={clsx('num font-medium', good ? 'text-emerald-700' : 'text-rose-700')}>
      {s > 0 ? '+' : ''}
      {formatMoney(value)}
    </span>
  );
}

function lastFinishedMonth() {
  const prev = addMonths(todayInTimezone(), -1);
  return prev.slice(0, 7);
}

function errorText(err: unknown) {
  if (!(err instanceof ApiError)) return 'Saving failed';
  return err.errors[0]?.message ?? err.message;
}

// ── Page ─────────────────────────────────────────────────────────────

export function MonthEndPage() {
  const [params, setParams] = useSearchParams();
  const ym = params.get('month') ?? lastFinishedMonth();
  const [year, month] = [Number(ym.slice(0, 4)), Number(ym.slice(5, 7))];
  const { can } = useAuth();

  const query = useQuery({
    queryKey: ['month-end', year, month],
    queryFn: () => api.get<MonthEndData>('/month-end', { year, month }),
  });
  const data = query.data;
  const canWrite = can('transactions.write') && !!data?.editable;
  const t = data?.totals;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Month-end actuals"
        description="Daily entries are estimates. Once a month is over, enter what was actually paid out, what reached the bank and the invalid-click credits to see the real profit."
        actions={
          <Input
            type="month"
            aria-label="Month"
            value={ym}
            max={todayInTimezone().slice(0, 7)}
            onChange={(e) =>
              e.target.value && setParams({ month: e.target.value }, { replace: true })
            }
            className="w-44"
          />
        }
      />

      {query.isLoading && <Spinner />}
      {query.isError && (
        <Alert tone="error">
          {query.error instanceof ApiError ? query.error.message : 'Failed to load'}
        </Alert>
      )}

      {data && t && (
        <>
          {!data.editable && (
            <Alert tone="info" title={`${monthName(month, true)} ${year} is not over yet`}>
              Showing estimates. Actuals can be entered from the 1st of next month.
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Compare
              label="Marketing spend (before GST)"
              estimated={t.estimatedSpend}
              actual={t.actualSpend}
              difference={t.spendDifference}
              inverse
            />
            <Compare
              label="Revenue"
              estimated={t.estimatedRevenue}
              actual={t.actualRevenue}
              difference={t.revenueDifference}
            />
            <Compare
              label="Profit (revenue − marketing)"
              estimated={t.estimatedProfit}
              actual={t.actualProfit}
              difference={t.profitDifference}
              emphasis
            />
          </div>
          <p className="flex items-center gap-1.5 text-xs text-slate-500">
            <Info className="size-3.5" />
            Payouts entered: {data.progress.revenueEntered}/{data.progress.revenueAccounts} (bank
            received {data.progress.revenueReceived})
            {data.progress.revenueCarried > 0 &&
              ` · ${data.progress.revenueCarried} below payment threshold`}{' '}
            · Ads credits entered: {data.progress.adsEntered}/{data.progress.adsAccounts}. Other
            expenses join the profit in phase 4.
          </p>

          <AdsCard data={data} canWrite={canWrite} />
          <RevenueCard data={data} canWrite={canWrite} />
          <AppsCard data={data} />
        </>
      )}
    </div>
  );
}

function Compare({
  label,
  estimated,
  actual,
  difference,
  inverse,
  emphasis,
}: {
  label: string;
  estimated: string;
  actual: string;
  difference: string;
  inverse?: boolean;
  emphasis?: boolean;
}) {
  return (
    <div
      className={clsx(
        'rounded-xl border bg-white p-4 shadow-sm',
        emphasis ? 'border-brand-200 ring-1 ring-brand-100' : 'border-slate-200',
      )}
    >
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <div className="mt-2 flex items-end gap-3">
        <div>
          <p className="text-[11px] tracking-wide text-slate-400 uppercase">Estimated</p>
          <p className="num text-base text-slate-600">{formatMoney(estimated)}</p>
        </div>
        <ArrowRight className="mb-1 size-4 text-slate-300" />
        <div>
          <p className="text-[11px] tracking-wide text-slate-400 uppercase">Actual</p>
          <p
            className={clsx(
              'num text-xl font-semibold',
              emphasis && sign(actual) < 0 ? 'text-rose-700' : 'text-slate-900',
            )}
          >
            {formatMoney(actual)}
          </p>
        </div>
      </div>
      <p className="mt-1 text-sm">
        <Diff value={difference} inverse={inverse} />
        <span className="ml-1 text-xs text-slate-400">difference</span>
      </p>
    </div>
  );
}

function AppsCell({ apps }: { apps: AppRef[] }) {
  if (!apps.length) return <span className="text-xs text-slate-400">No app</span>;
  return (
    <span className="flex items-center gap-2">
      <AppIcons apps={apps} size={24} />
      <span className="max-w-40 truncate text-xs text-slate-600">
        {apps.map((a) => a.name).join(', ')}
      </span>
    </span>
  );
}

function useSaver(year: number, month: number) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return (message: string) => ({
    onSuccess: () => {
      toast.success(message);
      void queryClient.invalidateQueries({ queryKey: ['month-end', year, month] });
    },
    onError: (err: unknown) => toast.error(errorText(err)),
  });
}

// ── Revenue ──────────────────────────────────────────────────────────

/** 'Aug, Sep 2026' for carried 'YYYY-MM' months. */
function carriedLabel(months: string[]) {
  const last = months[months.length - 1]!;
  return `${months.map((m) => monthName(Number(m.slice(5)))).join(', ')} ${last.slice(0, 4)}`;
}

const REV_STATUS = {
  NOT_ENTERED: { label: 'Not entered', tone: 'slate' },
  BELOW_THRESHOLD: { label: 'Below threshold', tone: 'blue' },
  BANK_PENDING: { label: 'Bank pending', tone: 'amber' },
  RECEIVED: { label: 'Received', tone: 'green' },
} as const;

function RevenueCard({ data, canWrite }: { data: MonthEndData; canWrite: boolean }) {
  return (
    <Card
      title="Revenue: actual payouts"
      description="Payout after network deductions (in the account currency) and the ₹ credited to your bank. The real exchange rate is worked out from the two."
      bodyClassName="p-0"
    >
      {data.revenue.length === 0 ? (
        <EmptyState title="No revenue recorded this month" />
      ) : (
        <div className="relative overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Account</th>
                <th className="px-4 py-3 text-right">Estimated</th>
                <th className="px-4 py-3 text-right">Actual payout</th>
                <th className="px-4 py-3 text-right">₹ received</th>
                <th className="px-4 py-3 text-right">Deduction</th>
                <th className="px-4 py-3 text-right">Rate est. → real</th>
                <th className="px-4 py-3 text-right">Difference (₹)</th>
                {canWrite && <th className="px-2 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.revenue.map((r) => (
                <RevenueRowView
                  key={r.account.id}
                  row={r}
                  year={data.year}
                  month={data.month}
                  canWrite={canWrite}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function RevenueRowView({
  row: r,
  year,
  month,
  canWrite,
}: {
  row: RevenueRow;
  year: number;
  month: number;
  canWrite: boolean;
}) {
  const cur = r.account.currencyCode;
  const inr = cur === 'INR';
  const [amount, setAmount] = useState(plain(r.settlement?.actualAmount));
  const [received, setReceived] = useState(plain(r.settlement?.actualInr));
  const [date, setDate] = useState(r.settlement?.receivedDate ?? '');
  useEffect(() => {
    setAmount(plain(r.settlement?.actualAmount));
    setReceived(plain(r.settlement?.actualInr));
    setDate(r.settlement?.receivedDate ?? '');
  }, [r.settlement]);
  const saver = useSaver(year, month);
  const path = `/month-end/revenue/${r.account.id}/${year}/${month}`;
  const save = useMutation({
    mutationFn: () =>
      api.put(path, {
        actualAmount: amount,
        actualInr: inr ? '' : received,
        receivedDate: date,
        updatedAt: r.settlement?.updatedAt,
      }),
    ...saver(`Saved actuals for ${r.account.accountName}`),
  });
  const clear = useMutation({
    mutationFn: () => api.delete(path),
    ...saver(`Cleared actuals for ${r.account.accountName}`),
  });
  const valid = AMOUNT_RE.test(amount) && (inr || received === '' || AMOUNT_RE.test(received));
  const dirty =
    amount !== plain(r.settlement?.actualAmount) ||
    (!inr && received !== plain(r.settlement?.actualInr)) ||
    date !== (r.settlement?.receivedDate ?? '');
  const st = REV_STATUS[r.status];

  return (
    <tr className={clsx(r.status === 'NOT_ENTERED' && canWrite && 'bg-amber-50/20')}>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2 font-medium text-slate-900">
          {r.account.accountName}
          <Badge tone={st.tone}>{st.label}</Badge>
        </div>
        <div className="text-xs text-slate-400">
          {[r.account.revenueSource.name, r.account.accountIdentifier, cur]
            .filter(Boolean)
            .join(' · ')}
        </div>
        <div className="mt-1">
          <AppsCell apps={r.apps} />
        </div>
        <div className="mt-1 text-[11px] text-slate-500">
          {r.bank ? (
            <>
              Bank: {r.bank.bankName}{' '}
              <span className="font-mono">{maskAccountNumber(r.bank.accountNumber)}</span>
              {r.bank.accountType && ` · ${LABELS.bankAccountType[r.bank.accountType]}`}
              {r.bank.accountHolderName && (
                <span className="block text-slate-400">{r.bank.accountHolderName}</span>
              )}
            </>
          ) : (
            <span className="text-slate-400">No payout bank linked</span>
          )}
        </div>
      </td>
      <td className="num px-4 py-2 text-right whitespace-nowrap">
        {!inr && <div>{formatMoney(r.estimatedAmount, cur)}</div>}
        <div className={clsx(!inr && 'text-xs text-slate-400')}>{formatMoney(r.estimatedInr)}</div>
        {r.carriedMonths.length > 0 && (
          <div className="mt-1 border-t border-slate-100 pt-1 text-[11px] text-slate-500">
            <div>
              + {formatMoney(r.carriedAmount, cur)} carried ({carriedLabel(r.carriedMonths)})
            </div>
            <div className="font-medium text-slate-700">
              = {formatMoney(r.balanceAmount, cur)} unpaid
            </div>
          </div>
        )}
        {r.payoutThreshold && (
          <div className="text-[11px] text-slate-400">
            pays at {formatMoney(r.payoutThreshold, cur)}
          </div>
        )}
      </td>
      <td className="px-4 py-2 text-right">
        {canWrite ? (
          <AmountInput
            label={`Actual payout for ${r.account.accountName}`}
            value={amount}
            onChange={setAmount}
            prefix={cur}
          />
        ) : (
          <span className="num">
            {r.settlement ? formatMoney(r.settlement.actualAmount, cur) : '—'}
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-right">
        {inr ? (
          <span className="text-xs text-slate-400">same as payout</span>
        ) : canWrite ? (
          <div className="flex flex-col items-end gap-1">
            <AmountInput
              label={`₹ received for ${r.account.accountName}`}
              value={received}
              onChange={setReceived}
              prefix="₹"
              placeholder="not yet"
            />
            <input
              type="date"
              aria-label={`Received date for ${r.account.accountName}`}
              value={date}
              max={todayInTimezone()}
              onChange={(e) => setDate(e.target.value)}
              className="h-7 w-28 rounded-md border border-slate-200 px-1 text-xs text-slate-600"
            />
          </div>
        ) : (
          <span className="num">
            {r.settlement?.actualInr ? formatMoney(r.settlement.actualInr) : '—'}
          </span>
        )}
      </td>
      <td className="num px-4 py-2 text-right whitespace-nowrap">
        {r.deduction == null ? (
          '—'
        ) : (
          <>
            <div>{formatMoney(r.deduction, cur)}</div>
            <div className="text-xs text-slate-400">{formatPercent(r.deductionPercent)}</div>
          </>
        )}
      </td>
      <td className="num px-4 py-2 text-right text-xs whitespace-nowrap">
        {inr ? (
          '—'
        ) : (
          <>
            ₹{formatDecimal(r.estimatedRate, 4)}
            {r.actualRate && (
              <span className="block font-medium text-slate-900">
                → ₹{formatDecimal(r.actualRate, 4)}
              </span>
            )}
          </>
        )}
      </td>
      <td className="px-4 py-2 text-right">
        {r.status === 'BELOW_THRESHOLD' ? (
          <span className="text-xs text-blue-600">Carried to next month</span>
        ) : (
          <Diff value={r.status === 'NOT_ENTERED' ? null : r.differenceInr} />
        )}
        {!inr && r.exchangeImpactInr != null && r.deductionImpactInr != null && (
          <div className="text-[11px] text-slate-400">
            deduction {formatMoney(r.deductionImpactInr)} · rate {formatMoney(r.exchangeImpactInr)}
          </div>
        )}
        {r.status === 'BANK_PENDING' && (
          <div className="text-[11px] text-amber-600">at estimated rate</div>
        )}
      </td>
      {canWrite && (
        <td className="px-2 py-2 text-right whitespace-nowrap">
          <RowActions
            canSave={valid && dirty}
            saving={save.isPending}
            onSave={() => save.mutate()}
            canClear={!!r.settlement}
            clearing={clear.isPending}
            onClear={() => clear.mutate()}
          />
        </td>
      )}
    </tr>
  );
}

// ── Ads ──────────────────────────────────────────────────────────────

function AdsCard({ data, canWrite }: { data: MonthEndData; canWrite: boolean }) {
  const mode = useAccountDisplayMode();
  return (
    <Card
      title="Ads: invalid-click credits"
      description="Enter the actual spend from the platform's invoice (before GST) or the invalid-click credit; the other is worked out from the estimated spend. GST and input credit are reduced in proportion."
      bodyClassName="p-0"
    >
      {data.ads.length === 0 ? (
        <EmptyState title="No ads spend recorded this month" />
      ) : (
        <div className="relative overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Account</th>
                <th className="px-4 py-3 text-right">Estimated spend</th>
                <th className="px-4 py-3 text-right">Actual spend</th>
                <th className="px-4 py-3 text-right">Credit</th>
                <th className="px-4 py-3 text-right text-violet-700">GST est. → actual</th>
                <th className="px-4 py-3 text-left">Status</th>
                {canWrite && <th className="px-2 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.ads.map((r) => (
                <AdsRowView
                  key={r.account.id}
                  row={r}
                  label={accountLabel(r.account, mode)}
                  year={data.year}
                  month={data.month}
                  canWrite={canWrite}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AdsRowView({
  row: r,
  label,
  year,
  month,
  canWrite,
}: {
  row: AdsRow;
  label: string;
  year: number;
  month: number;
  canWrite: boolean;
}) {
  const cur = r.account.currencyCode;
  const estimated = toDecimal(r.estimatedAmount);
  /** Actual spend = estimated − credit (account currency, before GST). */
  const actualFrom = (c: string) =>
    AMOUNT_RE.test(c) ? String(Number(estimated.minus(c).toString())) : '';
  const [credit, setCredit] = useState(plain(r.settlement?.invalidClickCredit));
  const [actual, setActual] = useState(
    r.settlement ? actualFrom(plain(r.settlement.invalidClickCredit)) : '',
  );
  useEffect(() => {
    setCredit(plain(r.settlement?.invalidClickCredit));
    setActual(r.settlement ? actualFrom(plain(r.settlement.invalidClickCredit)) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.settlement]);
  const onActual = (v: string) => {
    setActual(v);
    if (v === '') setCredit('');
    else if (AMOUNT_RE.test(v)) {
      const c = estimated.minus(v);
      setCredit(c.isNegative() ? '' : String(Number(c.toString())));
    }
  };
  const onCredit = (v: string) => {
    setCredit(v);
    setActual(v === '' ? '' : actualFrom(v));
  };
  const overEstimate = AMOUNT_RE.test(actual) && estimated.minus(actual).isNegative();
  const saver = useSaver(year, month);
  const path = `/month-end/ads/${r.account.id}/${year}/${month}`;
  const save = useMutation({
    mutationFn: () =>
      api.put(path, { invalidClickCredit: credit, updatedAt: r.settlement?.updatedAt }),
    ...saver(`Saved credit for ${r.account.accountName}`),
  });
  const clear = useMutation({
    mutationFn: () => api.delete(path),
    ...saver(`Cleared credit for ${r.account.accountName}`),
  });
  const valid = AMOUNT_RE.test(credit) && !overEstimate && !estimated.minus(credit).isNegative();
  const dirty = credit !== plain(r.settlement?.invalidClickCredit);

  return (
    <tr>
      <td className="px-4 py-2">
        <div className="font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-400">{r.account.platform.name}</div>
        <div className="mt-1">
          <AppsCell apps={r.apps} />
        </div>
      </td>
      <td className="num px-4 py-2 text-right whitespace-nowrap">
        {cur !== 'INR' && <div>{formatMoney(r.estimatedAmount, cur)}</div>}
        <div className={clsx(cur !== 'INR' && 'text-xs text-slate-400')}>
          {formatMoney(r.estimatedInr)}
        </div>
      </td>
      <td className="px-4 py-2 text-right">
        {canWrite ? (
          <AmountInput
            label={`Actual spend for ${r.account.accountName}`}
            value={actual}
            onChange={onActual}
            prefix={cur}
            placeholder={String(Number(r.estimatedAmount))}
          />
        ) : (
          <span className="num font-medium text-slate-900">{formatMoney(r.actualInr)}</span>
        )}
        {overEstimate ? (
          <div className="mt-0.5 text-xs text-rose-600">More than the estimated spend</div>
        ) : (
          cur !== 'INR' &&
          r.status === 'ENTERED' && (
            <div className="num mt-0.5 text-xs text-slate-400">{formatMoney(r.actualInr)}</div>
          )
        )}
      </td>
      <td className="px-4 py-2 text-right">
        {canWrite ? (
          <AmountInput
            label={`Invalid-click credit for ${r.account.accountName}`}
            value={credit}
            onChange={onCredit}
            prefix={cur}
          />
        ) : (
          <span className="num">
            {r.settlement ? formatMoney(r.settlement.invalidClickCredit, cur) : '—'}
          </span>
        )}
        {cur !== 'INR' && r.status === 'ENTERED' && (
          <div className="num mt-0.5 text-xs text-slate-400">{formatMoney(r.creditInr)}</div>
        )}
      </td>
      <td className="num px-4 py-2 text-right whitespace-nowrap text-violet-700">
        {formatMoney(r.estimatedGst)}
        {r.status === 'ENTERED' && (
          <span className="block text-xs">→ {formatMoney(r.actualGst)}</span>
        )}
      </td>
      <td className="px-4 py-2">
        <Badge tone={r.status === 'ENTERED' ? 'green' : 'slate'}>
          {r.status === 'ENTERED' ? 'Entered' : 'No credit'}
        </Badge>
      </td>
      {canWrite && (
        <td className="px-2 py-2 text-right whitespace-nowrap">
          <RowActions
            canSave={valid && dirty}
            saving={save.isPending}
            onSave={() => save.mutate()}
            canClear={!!r.settlement}
            clearing={clear.isPending}
            onClear={() => clear.mutate()}
          />
        </td>
      )}
    </tr>
  );
}

// ── Profit by app ────────────────────────────────────────────────────

function AppsCard({ data }: { data: MonthEndData }) {
  return (
    <Card
      title="Profit by app: before and after deductions"
      description="Shared accounts are split between apps by each app's share of the month's estimates."
      bodyClassName="p-0"
    >
      {data.apps.length === 0 ? (
        <EmptyState title="No data for this month" />
      ) : (
        <div className="relative overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">App</th>
                <th className="px-4 py-3 text-right">Spend est. → actual</th>
                <th className="px-4 py-3 text-right">Revenue est. → actual</th>
                <th className="px-4 py-3 text-right">Profit before</th>
                <th className="px-4 py-3 text-right">Profit after</th>
                <th className="px-4 py-3 text-right">Difference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.apps.map((a) => (
                <tr key={a.app.id}>
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-2.5">
                      <AppIcon src={a.app.iconUrl} name={a.app.name} size={28} />
                      <span>
                        <span className="block font-medium text-slate-900">{a.app.name}</span>
                        {a.app.developerName && (
                          <span className="block text-xs text-slate-400">
                            {a.app.developerName}
                          </span>
                        )}
                      </span>
                    </span>
                  </td>
                  <td className="num px-4 py-2 text-right whitespace-nowrap">
                    <span className="text-slate-400">{formatMoney(a.estimatedSpend)}</span>
                    <span className="block">{formatMoney(a.actualSpend)}</span>
                  </td>
                  <td className="num px-4 py-2 text-right whitespace-nowrap">
                    <span className="text-slate-400">{formatMoney(a.estimatedRevenue)}</span>
                    <span className="block">{formatMoney(a.actualRevenue)}</span>
                  </td>
                  <td className="num px-4 py-2 text-right whitespace-nowrap text-slate-600">
                    {formatMoney(a.estimatedProfit)}
                  </td>
                  <td
                    className={clsx(
                      'num px-4 py-2 text-right font-semibold whitespace-nowrap',
                      sign(a.actualProfit) < 0 ? 'text-rose-700' : 'text-emerald-700',
                    )}
                  >
                    {formatMoney(a.actualProfit)}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Diff value={a.profitDifference} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 bg-slate-50/60 font-semibold">
              <tr>
                <td className="px-4 py-2">Total</td>
                <td className="num px-4 py-2 text-right">{formatMoney(data.totals.actualSpend)}</td>
                <td className="num px-4 py-2 text-right">
                  {formatMoney(data.totals.actualRevenue)}
                </td>
                <td className="num px-4 py-2 text-right">
                  {formatMoney(data.totals.estimatedProfit)}
                </td>
                <td className="num px-4 py-2 text-right">
                  {formatMoney(data.totals.actualProfit)}
                </td>
                <td className="px-4 py-2 text-right">
                  <Diff value={data.totals.profitDifference} />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Small pieces ─────────────────────────────────────────────────────

function AmountInput({
  label,
  value,
  onChange,
  prefix,
  placeholder = '—',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  prefix: string;
  placeholder?: string;
}) {
  const invalid = value !== '' && !AMOUNT_RE.test(value);
  return (
    <label className="inline-flex items-center gap-1">
      <span className="text-xs text-slate-400">{prefix}</span>
      <input
        aria-label={label}
        inputMode="decimal"
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.replace(/,/g, '').trim())}
        onFocus={(e) => e.target.select()}
        className={clsx(
          'num h-9 w-28 rounded-md border px-2 text-right focus:ring-2 focus:outline-none',
          invalid
            ? 'border-rose-400 focus:ring-rose-200'
            : 'border-slate-300 focus:border-brand-500 focus:ring-brand-500/20',
        )}
      />
    </label>
  );
}

function RowActions({
  canSave,
  saving,
  onSave,
  canClear,
  clearing,
  onClear,
}: {
  canSave: boolean;
  saving: boolean;
  onSave: () => void;
  canClear: boolean;
  clearing: boolean;
  onClear: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <Button
        size="sm"
        icon={<Save className="size-4" />}
        disabled={!canSave}
        loading={saving}
        onClick={onSave}
      >
        Save
      </Button>
      {canClear && (
        <button
          type="button"
          onClick={onClear}
          disabled={clearing}
          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Clear actuals"
          title="Clear actuals (back to estimates)"
        >
          <RotateCcw className="size-4" />
        </button>
      )}
    </span>
  );
}
